// Rebuild the pre-band demand for one (day, shape) group — display only.
//
// The fixture's shape_of_day is the CLAMPED demand (band mins added, maxs removed).
// To show "before, with the band's changes on top", the demand grid needs the
// original staircase back: subtract the band-added intervals, restore the removed
// ones, then re-tile into stacked rows. The audit request still sends the clamped
// demand — this module never touches state, only what the grid draws.

const toMin = (t) => {
  if (typeof t !== 'string' || !t.includes(':')) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (Number.isFinite(m) ? m : 0);
};

const toHHMM = (m) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

// blocks: clamped shape_of_day rows for one (day, shape). deltas: that group's band
// intervals ({direction, start, end, heads}). Returns original-demand blocks shaped
// like shape_of_day rows (template fields from the first block, or `fallback`).
export const originalDemandBlocks = (blocks, deltas, fallback = {}) => {
  const spans = (blocks || [])
    .map((b) => [toMin(b.start), toMin(b.end)])
    .filter(([s, e]) => s != null && e != null && e > s);
  const changes = (deltas || [])
    .map((d) => [toMin(d.start), toMin(d.end), (d.direction === 'added' ? -1 : 1) * (d.heads || 0)])
    .filter(([s, e]) => s != null && e != null && e > s);
  if (!changes.length) return blocks || [];

  const edges = [...spans.flat(), ...changes.flatMap(([s, e]) => [s, e])];
  const lo = Math.min(...edges);
  const hi = Math.max(...edges);
  const arr = new Array(hi - lo).fill(0);
  spans.forEach(([s, e]) => {
    for (let i = s; i < e; i += 1) arr[i - lo] += 1;
  });
  changes.forEach(([s, e, diff]) => {
    for (let i = s; i < e; i += 1) arr[i - lo] = Math.max(0, arr[i - lo] + diff);
  });

  const template = blocks && blocks.length ? blocks[0] : { type: 'Staff', ...fallback };
  const out = [];
  const maxLevel = Math.max(0, ...arr);
  for (let level = 1; level <= maxLevel; level += 1) {
    let start = null;
    for (let i = 0; i <= arr.length; i += 1) {
      const on = i < arr.length && arr[i] >= level;
      if (on && start == null) start = i;
      if (!on && start != null) {
        out.push({
          ...template,
          start: toHHMM(start + lo),
          end: toHHMM(i + lo),
          length: (i - start) / 60,
        });
        start = null;
      }
    }
  }
  return out;
};
